import { Modal as AntModal, ModalProps as AntModalProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { Button } from '../Button';
import { radius } from '../../theme/radius';

export type ModalSize = 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';

export interface ModalProps extends Omit<
  AntModalProps,
  'width' | 'okText' | 'cancelText' | 'onOk' | 'onCancel'
> {
  /** Whether the modal is visible */
  open: boolean;
  /** Modal title */
  title?: React.ReactNode;
  /** Size of the modal */
  size?: ModalSize;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when confirm button is clicked */
  onConfirm?: () => void;
  /** Text for confirm button */
  confirmText?: string;
  /** Text for cancel button */
  cancelText?: string;
  /** Whether confirm button is loading */
  confirmLoading?: boolean;
  /** Whether confirm button is disabled */
  confirmDisabled?: boolean;
  /** Whether to show footer */
  showFooter?: boolean;
  /** Custom footer content (overrides default footer) */
  footer?: React.ReactNode;
  /** Whether to show close icon */
  closable?: boolean;
  /** Whether clicking mask closes modal */
  maskClosable?: boolean;
  /** Whether the confirm action is destructive */
  danger?: boolean;
  /**
   * Override `.ant-modal-body`'s padding (default: `paddingLG` on all
   * sides). Pass `0` when the content wants full control over its own
   * internal spacing (mirrors how `UpgradePromptModal` zeroes antd's own
   * body padding) instead of fighting the shared default from outside.
   */
  bodyPadding?: string | number;
  /** Modal content */
  children?: React.ReactNode;
}

const sizeMap: Record<ModalSize, number | string> = {
  small: 400,
  medium: 560,
  large: 800,
  xlarge: 900,
  fullscreen: '100vw',
};

interface StyledModalProps {
  $token: GlobalToken;
  $size: ModalSize;
  $bodyPadding?: string | number;
}

const StyledModal = styled(AntModal)<StyledModalProps>`
  font-family: ${({ $token }) => $token.fontFamily};

  ${({ $size }) =>
    $size === 'fullscreen' &&
    `
    top: 0;
    max-width: 100vw;
    margin: 0;
    padding: 0;

    .ant-modal-content {
      min-height: 100vh;
      border-radius: 0;
    }
  `}

  .ant-modal-content {
    border-radius: ${radius.box}px;
    overflow: hidden;
    box-shadow: ${({ $token }) => $token.boxShadow};
  }

  .ant-modal-header {
    padding: ${({ $token }) =>
      `${$token.paddingLG - 4}px ${$token.paddingLG}px`};
    margin: 0;
    border-bottom: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    background: ${({ $token }) => $token.colorBgContainer};
  }

  .ant-modal-title {
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeLG}px;
    font-weight: ${({ $token }) => $token.fontWeightStrong};
    color: ${({ $token }) => $token.colorText};
    line-height: 1.4;
  }

  .ant-modal-close {
    top: ${({ $token }) => $token.paddingLG - 4}px;
    right: ${({ $token }) => $token.paddingLG - 4}px;
    width: ${({ $token }) => $token.controlHeightSM}px;
    height: ${({ $token }) => $token.controlHeightSM}px;
    border-radius: ${({ $token }) => $token.borderRadius}px;
    transition: all ${({ $token }) => $token.motionDurationMid} ease;

    &:hover {
      background: ${({ $token }) => $token.colorBgLayout};
    }

    .ant-modal-close-x {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${({ $token }) => $token.controlHeightSM}px;
      height: ${({ $token }) => $token.controlHeightSM}px;
      font-size: ${({ $token }) => $token.fontSize}px;
      color: ${({ $token }) => $token.colorTextTertiary};
    }
  }

  .ant-modal-body {
    padding: ${({ $token, $bodyPadding }) =>
      $bodyPadding !== undefined
        ? typeof $bodyPadding === 'number'
          ? `${$bodyPadding}px`
          : $bodyPadding
        : `${$token.paddingLG}px`};
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSize}px;
    color: ${({ $token }) => $token.colorTextSecondary};
    line-height: 1.6;
  }

  .ant-modal-footer {
    padding: ${({ $token }) => `${$token.padding}px ${$token.paddingLG}px`};
    margin: 0;
    border-top: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    background: ${({ $token }) => $token.colorBgLayout};
  }
`;

interface FooterWrapperProps {
  $token: GlobalToken;
}

const FooterWrapper = styled.div<FooterWrapperProps>`
  display: flex;
  justify-content: flex-end;
  gap: ${({ $token }) => $token.paddingSM}px;
`;

/**
 * Modal component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Modal } from '@libertify/ui';
 *
 * const [open, setOpen] = useState(false);
 *
 * <Modal
 *   open={open}
 *   title="Confirm Action"
 *   onClose={() => setOpen(false)}
 *   onConfirm={() => { doSomething(); setOpen(false); }}
 *   confirmText="Confirm"
 *   cancelText="Cancel"
 * >
 *   Are you sure you want to proceed?
 * </Modal>
 * ```
 */
export const Modal = ({
  open,
  title,
  size = 'medium',
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmLoading = false,
  confirmDisabled = false,
  showFooter = true,
  footer,
  closable = true,
  maskClosable = true,
  danger = false,
  bodyPadding,
  children,
  ...props
}: ModalProps) => {
  const { token } = theme.useToken();

  // Default footer with our Button components
  const defaultFooter = (
    <FooterWrapper $token={token}>
      <Button variant="secondary" onClick={onClose}>
        {cancelText}
      </Button>
      {onConfirm && (
        <Button
          variant="primary"
          onClick={onConfirm}
          loading={confirmLoading}
          disabled={confirmDisabled}
          style={danger ? { background: token.colorError } : undefined}
        >
          {confirmText}
        </Button>
      )}
    </FooterWrapper>
  );

  // Determine what footer to show
  const getModalFooter = () => {
    if (showFooter) {
      return footer === undefined ? defaultFooter : footer;
    }
    return null;
  };
  const modalFooter = getModalFooter();

  return (
    <StyledModal
      $token={token}
      $size={size}
      $bodyPadding={bodyPadding}
      open={open}
      title={title}
      width={sizeMap[size]}
      onCancel={onClose}
      closable={closable}
      maskClosable={maskClosable}
      footer={modalFooter}
      centered={size !== 'fullscreen'}
      {...props}
    >
      {children}
    </StyledModal>
  );
};

export default Modal;
